import { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';
const money = (n) => n == null ? '—' : '$' + Math.round(n).toLocaleString();

const s = {
  wrap: { padding: 32, maxWidth: 1200, margin: '0 auto' },
  h1: { margin: '0 0 4px', fontSize: 24, color: '#336090' },
  sub: { margin: '0 0 20px', color: '#666', fontSize: 14 },
  card: { background: 'white', borderRadius: 12, padding: 18, marginBottom: 18, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: 15, fontWeight: 800, color: '#336090', marginBottom: 12 },
  pill: { display: 'inline-block', padding: '2px 10px', borderRadius: 10, fontSize: 11, fontWeight: 800 },
  statRow: { display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 18 },
  stat: { flex: 1, minWidth: 180, background: 'white', borderRadius: 12, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  statLabel: { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' },
  statValue: { fontSize: 22, fontWeight: 800, color: '#336090', marginTop: 2 },
};

const TIER = {
  green:   { bg: '#dcfce7', fg: '#166534', label: '🟢 HEALTHY' },
  watch:   { bg: '#fef9c3', fg: '#854d0e', label: '🟡 WATCH' },
  stalled: { bg: '#ffedd5', fg: '#9a3412', label: '🟠 STALLED' },
  fight:   { bg: '#fee2e2', fg: '#991b1b', label: '🔴 FIGHT' },
};

export default function Money({ user }) {
  const [forecast, setForecast] = useState([]);
  const [reportCard, setReportCard] = useState(null);
  const [owed, setOwed] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const headers = { Authorization: 'Bearer ' + user.token };
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState('');

  const load = useCallback(async () => {
    if (!facilityId) return;
    const [f, o, rc] = await Promise.all([
      fetch(API + '/api/forecast?facilityId=' + facilityId, { headers }).then(x => x.json()).catch(() => ({})),
      fetch(API + '/api/money-owed?facilityId=' + facilityId, { headers }).then(x => x.json()).catch(() => ({})),
      fetch(API + '/api/forecast/report-card?facilityId=' + facilityId, { headers }).then(x => x.json()).catch(() => ({})),
    ]);
    if (f.ok) setForecast(f.data || []);
    if (o.ok) setOwed(o);
    if (rc.ok) setReportCard(rc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(API + '/api/facilities', { headers });
        const j = await r.json();
        if (j.ok) { setFacilities(j.data); setFacilityId(prev => prev || ((j.data[0] || {}).id || '')); }
      } catch (e) {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Weekly totals for the chart
  const weeks = {};
  const upcoming = forecast.filter(r => r.actual === null || r.actual === undefined);
  upcoming.forEach(r => {
    if (!weeks[r.week_start]) weeks[r.week_start] = 0;
    weeks[r.week_start] += parseFloat(r.predicted || 0);
  });
  const weekList = Object.entries(weeks).sort((a, b) => a[0].localeCompare(b[0])).slice(0, 8);
  const maxWeek = Math.max(1, ...weekList.map(([, v]) => v));

  const pulseData = owed ? owed.data : [];

  // Payer × week matrix (same 8 weeks as the chart)
  const wkKeys = weekList.map(([wk]) => wk);
  const byPayer = {};
  upcoming.forEach(r => {
    if (!wkKeys.includes(r.week_start)) return;
    const p = r.payer_name || '—';
    if (!byPayer[p]) byPayer[p] = { total: 0, weeks: {} };
    const v = parseFloat(r.predicted || 0);
    byPayer[p].weeks[r.week_start] = (byPayer[p].weeks[r.week_start] || 0) + v;
    byPayer[p].total += v;
  });
  const calRow = byPayer['CALIBRATION']; delete byPayer['CALIBRATION'];
  const payerRows = Object.entries(byPayer).sort((a, b) => b[1].total - a[1].total);
  const topRows = payerRows.slice(0, 12);
  const restRows = payerRows.slice(12);
  const others = { total: 0, weeks: {} };
  restRows.forEach(([, d]) => {
    others.total += d.total;
    Object.entries(d.weeks).forEach(([w, v]) => { others.weeks[w] = (others.weeks[w] || 0) + v; });
  });
  const wkShort = (wk) => new Date(String(wk).slice(0,10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const rcRows = (reportCard && reportCard.data) || [];
  const rcMax = Math.max(1, ...rcRows.map(r => Math.max(parseFloat(r.predicted || 0), parseFloat(r.actual || 0))));
  const rcBias = reportCard ? reportCard.bias_pct : null;
  const cellFmt = (v) => !v || v < 1 ? '' : v >= 1000 ? '$' + Math.round(v/1000) + 'K' : '$' + Math.round(v);

  return (
    <div style={s.wrap}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={s.h1}>Money</h1>
          <p style={s.sub}>Predicted cash, payer health, and the fight list. Forecast excludes payers that aren't actually paying — the fight list is where those live.</p>
        </div>
        <select value={facilityId} onChange={e => setFacilityId(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, fontWeight: 600, background: '#fff', cursor: 'pointer', marginTop: 4 }}>
          <option value="all">All Facilities</option>
          {facilities.map(f => <option key={f.id} value={f.id}>{f.org_name}</option>)}
        </select>
      </div>

      {owed && (
        <div style={s.statRow}>
          <div style={s.stat}><div style={s.statLabel}>Total Open AR (billed)</div><div style={s.statValue}>{money(owed.totals.open_billed)}</div></div>
          <div style={s.stat}><div style={s.statLabel}>Realistic Value</div><div style={s.statValue}>{money(owed.totals.realistic_value)}</div></div>
          <div style={s.stat}><div style={s.statLabel}>🔴 Fight Exposure</div><div style={{ ...s.statValue, color: '#991b1b' }}>{money(owed.totals.fight_billed)}</div></div>
          <div style={s.stat}><div style={s.statLabel}>🟠 Stalled Exposure</div><div style={{ ...s.statValue, color: '#9a3412' }}>{money(owed.totals.stalled_billed)}</div></div>
        </div>
      )}

      <div style={s.card}>
        <div style={s.cardTitle}>📈 Predicted Deposits — next 8 weeks (all streams)</div>
        {false ? (
          <div style={{ color: '#94a3b8', fontSize: 13 }}>
            Forecasting is per-facility, built from this facility's own deposit history — the payer health and fight list above ARE this facility's real numbers.
          </div>
        ) : weekList.length === 0 ? <div style={{ color: '#94a3b8', fontSize: 13 }}>No forecast yet — runs nightly at 1:30am.</div> : (
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', height: 180 }}>
            {weekList.map(([wk, val]) => (
              <div key={wk} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#336090', marginBottom: 4 }}>{money(val)}</div>
                <div style={{ width: '70%', background: 'linear-gradient(180deg,#3b82f6,#336090)', borderRadius: '6px 6px 0 0', height: Math.max(4, (val / maxWeek) * 130) }} />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>{new Date(String(wk).slice(0,10) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>Week of Sunday. Predictions get graded against actual deposits as weeks close — accuracy improves automatically.</div>
      </div>

      {rcRows.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>🎯 Forecast Report Card — predicted vs actual, week by week</div>
          {rcBias != null && (
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: rcBias < 0 ? '#9a3412' : '#166534' }}>
              Over {reportCard.graded_weeks} graded week{reportCard.graded_weeks === 1 ? '' : 's'}, the model has run {Math.abs(rcBias)}% {rcBias > 0 ? 'LOW' : 'high'} on average{rcBias > 0 ? ' — actual cash has beaten the prediction.' : '.'}
            </div>
          )}
          <div style={{ display: 'flex', gap: 16, overflowX: 'auto', paddingBottom: 8 }}>
            {rcRows.map(r => {
              const pred = parseFloat(r.predicted || 0);
              const act = r.actual == null ? null : parseFloat(r.actual);
              const miss = pred > 0 && act != null ? Math.round(((act - pred) / pred) * 100) : null;
              return (
                <div key={r.week_start} style={{ minWidth: 96, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, height: 16, color: miss == null ? '#94a3b8' : miss >= 0 ? '#166534' : '#9a3412' }}>
                    {r.is_partial ? 'in progress' : miss == null ? (pred > 0 ? 'no actual' : 'no forecast') : (miss > 0 ? '+' : '') + miss + '%'}
                  </div>
                  <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end', height: 130, marginTop: 4 }}>
                    <div title={'Predicted ' + money(pred)} style={{ width: 26, borderRadius: '5px 5px 0 0', background: pred > 0 ? 'linear-gradient(180deg,#3b82f6,#336090)' : '#e2e8f0', height: Math.max(4, (pred / rcMax) * 126) }} />
                    <div title={'Actual ' + (act == null ? '—' : money(act))} style={{ width: 26, borderRadius: '5px 5px 0 0', background: act == null ? '#f1f5f9' : r.is_partial ? 'repeating-linear-gradient(45deg,#86efac,#86efac 5px,#bbf7d0 5px,#bbf7d0 10px)' : 'linear-gradient(180deg,#22c55e,#15803d)', border: act == null ? '1px dashed #cbd5e1' : 'none', height: Math.max(4, ((act == null ? 0 : act) / rcMax) * 126) }} />
                  </div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#334155', marginTop: 5, whiteSpace: 'nowrap' }}>{money(pred) === '$0' ? '—' : money(pred)} / {act == null ? '—' : money(act)}</div>
                  <div style={{ fontSize: 10.5, color: '#64748b', marginTop: 2 }}>{wkShort(r.week_start)}</div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
            Blue = what the model predicted (frozen the day before the week started). Green = actual deposits entered in Collections. Striped = week still in progress. Weeks before the model existed show actuals only. Late Collections entries make the model look worse than it is — enter deposits same-day.
          </div>
        </div>
      )}

      {topRows.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>💵 Who Pays When — by payer, next 8 weeks</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
              <thead><tr>
                <th style={{ textAlign: 'left', padding: '7px 10px', fontSize: 11, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb' }}>Payer</th>
                {wkKeys.map(wk => <th key={wk} style={{ textAlign: 'right', padding: '7px 8px', fontSize: 11, color: '#666', borderBottom: '2px solid #e5e7eb', whiteSpace: 'nowrap' }}>{wkShort(wk)}</th>)}
                <th style={{ textAlign: 'right', padding: '7px 10px', fontSize: 11, color: '#336090', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb' }}>8-wk Total</th>
              </tr></thead>
              <tbody>
                {topRows.map(([payer, d]) => (
                  <tr key={payer}>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', fontWeight: 700, whiteSpace: 'nowrap' }}>{payer.length > 30 ? payer.slice(0, 30) + '…' : payer}</td>
                    {wkKeys.map(wk => <td key={wk} style={{ padding: '8px 8px', borderBottom: '1px solid #f0f4f8', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#334155' }}>{cellFmt(d.weeks[wk])}</td>)}
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', textAlign: 'right', fontWeight: 800, color: '#166534', fontVariantNumeric: 'tabular-nums' }}>{money(d.total)}</td>
                  </tr>
                ))}
                {others.total >= 1 && (
                  <tr>
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', fontWeight: 600, color: '#64748b' }}>All others ({restRows.length})</td>
                    {wkKeys.map(wk => <td key={wk} style={{ padding: '8px 8px', borderBottom: '1px solid #f0f4f8', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#64748b' }}>{cellFmt(others.weeks[wk])}</td>)}
                    <td style={{ padding: '8px 10px', borderBottom: '1px solid #f0f4f8', textAlign: 'right', fontWeight: 700, color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{money(others.total)}</td>
                  </tr>
                )}
                {calRow && (
                  <tr style={{ background: '#f8fafc' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#7c3aed', fontStyle: 'italic' }}>Calibration (model tuning)</td>
                    {wkKeys.map(wk => <td key={wk} style={{ padding: '8px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#7c3aed', fontStyle: 'italic' }}>{cellFmt(calRow.weeks[wk])}</td>)}
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontVariantNumeric: 'tabular-nums', fontStyle: 'italic' }}>{money(calRow.total)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>Blended across all streams (open AR, census, authorized days). Calibration row aligns the model to trailing actual deposits and shrinks automatically as grading data accumulates. Columns match the chart above.</div>
        </div>
      )}

      <div style={s.card}>
        <div style={s.cardTitle}>🩺 Payer Pulse</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {pulseData.map(p => (
            <div key={p.payer} onClick={() => setExpanded(expanded === p.payer ? null : p.payer)}
              style={{ cursor: 'pointer', padding: '8px 12px', borderRadius: 10, background: TIER[p.tier].bg, border: '1px solid ' + TIER[p.tier].fg + '33', minWidth: 150 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: TIER[p.tier].fg }}>{p.payer.length > 24 ? p.payer.slice(0, 24) + '…' : p.payer}</div>
              <div style={{ fontSize: 12, color: '#334155' }}>{money(p.open_billed)} open</div>
            </div>
          ))}
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>⚔️ Money Owed — Fight List</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr>{['Payer', 'Tier', 'Billed Open', 'Realistic Value', 'Claims', 'Last Payment', 'Days Silent'].map(h =>
            <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #e5e7eb' }}>{h}</th>)}</tr></thead>
          <tbody>
            {pulseData.map(p => (
              <tr key={p.payer} style={{ background: p.tier === 'fight' ? '#fef2f2' : p.tier === 'stalled' ? '#fff7ed' : 'transparent' }}>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8', fontWeight: 700 }}>{p.payer}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8' }}>
                  <span style={{ ...s.pill, background: TIER[p.tier].bg, color: TIER[p.tier].fg }}>{TIER[p.tier].label}</span>
                </td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8' }}>{money(p.open_billed)}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8', fontWeight: 700, color: '#166534' }}>{money(p.realistic_value)}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8' }}>{p.open_claims}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8' }}>{p.last_payment ? new Date(p.last_payment).toLocaleDateString('en-US') : <span style={{ color: '#991b1b', fontWeight: 800 }}>NEVER</span>}</td>
                <td style={{ padding: '9px 10px', borderBottom: '1px solid #f0f4f8', color: p.days_silent > 120 ? '#991b1b' : '#334155', fontWeight: p.days_silent > 120 ? 800 : 400 }}>
                  {p.days_silent >= 99999 ? '—' : p.days_silent + 'd'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>Tiers are computed from each payer's own historical payment cadence. Billed amounts are chargemaster; Realistic Value uses each payer's actual collection rate.</div>
      </div>
    </div>
  );
}
